import { Icon } from '@chakra-ui/icons'
import { Flex, HStack } from '@chakra-ui/layout'
import { Tooltip, Text } from '@chakra-ui/react'
import { Base64 } from 'js-base64'
import type { ReactElement } from 'react'
import React, { useContext, useMemo } from 'react'
import { BsFolder2 } from 'react-icons/bs'
import { Link, useHistory } from 'react-router-dom'
import { useGetQueryParams } from '../../../../hooks/useGetQueryParams'
import type { MediaLibrarySearchFolderResponse } from '../../../../interfaces'
import { MLContext } from '../../../../providers'
import { MediaLibraryActions, MediaLibraryActionType } from '../../../../providers/MLProvider'
import { ContextMenuTrigger } from '../../ContextMenu'
import { colors } from '../../../../../ui-kit'

interface MediaLibrarySearchFolderResponseWithSelection
  extends MediaLibrarySearchFolderResponse {
    onSelect(idx: number, shiftKeyPressed: boolean): void
    idx: number
    isFolderSelected: boolean
    dispatch: React.Dispatch<MediaLibraryActionType | MediaLibraryActionType[]>
    currentMediaCollection?: string,
    mediaCollectionRoute?: boolean,
}

const FolderElement = (
  folder: MediaLibrarySearchFolderResponseWithSelection,
): ReactElement => {
  const {
    name,
    folderPath,
    onSelect,
    idx,
    isFolderSelected,
    currentMediaCollection,
    mediaCollectionRoute,
    dispatch,
  } = folder

  const history = useHistory()

  const queryFromUrl = useGetQueryParams()

  const handleClick: React.MouseEventHandler<HTMLDivElement> = e => {
    e.stopPropagation()
    e.preventDefault()
    if (onSelect) {
      onSelect(idx, e.shiftKey ? true : false)
    }

    dispatch([
      {
        type: MediaLibraryActions.SELECT_FOLDER,
        payload: {
          folder,
          event: e,
        },
      },
      {
        type: MediaLibraryActions.DESELECT_ALL_MEDIA_COLLECTIONS,
      },
    ])
  }

  const handleDoubleClick: React.MouseEventHandler<HTMLDivElement> = () => {
    dispatch([
      {
        type: MediaLibraryActions.DESELECT_ALL_FILES_AND_FOLDERS,
      },
      {
        type: MediaLibraryActions.RESET_FILE_PAGE,
      },
      {
        type: MediaLibraryActions.RESET_FOLDER_PAGE,
      },
    ])
    mediaCollectionRoute
      ? history.push(
          `/media-collections/${currentMediaCollection}/${Base64.encode(
            folderPath,
            true,
          )}${queryFromUrl as string}`,
        )
      : history.push(
          `/media-library/${Base64.encode(folderPath, true)}${
            queryFromUrl as string
          }`,
        )
  }

  return (
    <ContextMenuTrigger
      onTrigger={event => {
        if (!isFolderSelected) {
          dispatch([
            {
              type: MediaLibraryActions.SELECT_FOLDER,
              payload: {
                folder,
                event,
              },
            },
            {
              type: MediaLibraryActions.DESELECT_ALL_MEDIA_COLLECTIONS,
            },
          ])
        }
      }}
      dispatch={dispatch}
    >
      {contextMenuProps => (
        <Tooltip label={name} placement="top-start">
          <HStack
            paddingX={5}
            paddingY={3}
            borderColor={isFolderSelected ? 'appClearBlue' : 'appPaleLilac'}
            borderWidth="1px"
            borderRadius="lg"
            width="100%"
            alignItems="center"
            spacing={2}
            cursor="pointer"
            onClick={handleClick}
            onDoubleClick={handleDoubleClick}
            as={Link}
            to={`/media-library/${Base64.encode(folderPath, true)}`}
            overflow="hidden"
            {...contextMenuProps}
          >
            <Icon as={BsFolder2} boxSize={8} color="appDarkIndigo" />
            <Flex width="90%" overflow="hidden">
              <Text className="asset-name" noOfLines={1} width="100%" color={colors.appDarkIndigo}>
                {name}
              </Text>
            </Flex>
          </HStack>
        </Tooltip>
      )}
    </ContextMenuTrigger>
  )
}

export default React.memo(FolderElement)
